// Neo product changes in the admin console (ADR 0231): the wire shapes of
// /v1/admin/neo/changes*, the defensive decoder for drafted cards on the admin
// Neo stream, and how a phase is shown. Pure, so it is tested under Node.

export type NeoChangeState = 'open' | 'merged' | 'closed';
export type NeoChangePhase =
  | 'running'
  | 'failed'
  | 'pr_open'
  | 'deploying'
  | 'live'
  | 'deploy_failed'
  | 'closed';

export interface NeoChange {
  id: string;
  title: string;
  request: string;
  state: NeoChangeState;
  prNumber: number | null;
  mergeSha: string | null;
  mergedAt: string | null;
  mergedBy: string | null;
  requestedBy: string | null;
  conversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowRun {
  status: string;
  conclusion: string | null;
  url: string;
  createdAt: string;
}

export interface NeoChangeStatus {
  phase: NeoChangePhase;
  run: WorkflowRun | null;
  pr: {
    number: number;
    url: string;
    title: string;
    headSha: string;
    mergeableState: string | null;
    changedFiles: number;
    additions: number;
    deletions: number;
    files: string[];
    sensitiveFiles: string[];
  } | null;
  ci: 'none' | 'pending' | 'success' | 'failure';
  behind: boolean;
  conflicted: boolean;
  canMerge: boolean;
  mergeBlockedReason: string | null;
  deploy: WorkflowRun | null;
}

export interface NeoChangeDetail {
  change: NeoChange;
  status: NeoChangeStatus | null;
  statusError: string | null;
}

/** A card Neo drafted. Nothing runs until the admin clicks Start or Revise. */
export interface NeoChangeDraft {
  /** The stored card's id (ADR 0251); null when the turn could not be stored. */
  id: string | null;
  kind: 'new' | 'revise';
  changeId: string | null;
  title: string;
  request: string;
  /** Once started: the change it created or revised. */
  usedChangeId: string | null;
}

/**
 * Decode drafted cards, from the `change_drafts` frame or a reopened chat;
 * anything malformed is dropped, never shown half-built.
 */
export function parseChangeDrafts(value: unknown): NeoChangeDraft[] {
  if (!Array.isArray(value)) return [];
  const drafts: NeoChangeDraft[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) continue;
    const d = item as Record<string, unknown>;
    const kind = d.kind === 'new' || d.kind === 'revise' ? d.kind : null;
    if (kind === null || typeof d.title !== 'string' || typeof d.request !== 'string') continue;
    const changeId = typeof d.changeId === 'string' ? d.changeId : null;
    if (kind === 'revise' && changeId === null) continue;
    drafts.push({
      id: typeof d.id === 'string' ? d.id : null,
      kind,
      changeId,
      title: d.title,
      request: d.request,
      usedChangeId: typeof d.usedChangeId === 'string' ? d.usedChangeId : null,
    });
  }
  return drafts;
}

/** One message of a reopened admin Neo chat (GET /v1/admin/neo/conversation). */
export interface StoredChatMessage {
  role: 'user' | 'assistant';
  content: string;
  drafts: NeoChangeDraft[];
}

/**
 * The reopened chat's messages, with each reply's stored cards. A message
 * stored before cards were kept has none; its text still carries the
 * "(Drafted a change: …)" note, which is shown as it is.
 */
export function parseStoredMessages(value: unknown): StoredChatMessage[] {
  if (!Array.isArray(value)) return [];
  const messages: StoredChatMessage[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) continue;
    const m = item as Record<string, unknown>;
    if ((m.role !== 'user' && m.role !== 'assistant') || typeof m.content !== 'string') continue;
    messages.push({ role: m.role, content: m.content, drafts: parseChangeDrafts(m.drafts) });
  }
  return messages;
}

/** The change a used card links to, from a 409 `draft_already_used` refusal. */
export function usedChangeFromError(err: unknown): string | null {
  const body = (err as { body?: { error?: unknown; changeId?: unknown } } | null)?.body;
  return body?.error === 'draft_already_used' && typeof body.changeId === 'string'
    ? body.changeId
    : null;
}

const PHASES: Record<NeoChangePhase, { label: string; badge: string }> = {
  running: { label: 'Neo is working', badge: 'running' },
  failed: { label: 'Failed', badge: 'failed' },
  pr_open: { label: 'PR open', badge: 'suggested' },
  deploying: { label: 'Deploying', badge: 'running' },
  live: { label: 'Live', badge: 'succeeded' },
  deploy_failed: { label: 'Deploy failed', badge: 'failed' },
  closed: { label: 'Closed', badge: 'rejected' },
};

export function phaseLabel(phase: NeoChangePhase): { label: string; badge: string } {
  return PHASES[phase];
}

/** The list shows cloud's own state; the detail page shows the live phase. */
export function stateLabel(state: NeoChangeState): { label: string; badge: string } {
  if (state === 'merged') return { label: 'Merged', badge: 'succeeded' };
  if (state === 'closed') return { label: 'Closed', badge: 'rejected' };
  return { label: 'Open', badge: 'running' };
}

export function ciLabel(ci: NeoChangeStatus['ci']): { label: string; badge: string } {
  if (ci === 'success') return { label: 'CI gate passed', badge: 'succeeded' };
  if (ci === 'failure') return { label: 'CI gate failed', badge: 'failed' };
  if (ci === 'pending') return { label: 'CI gate running', badge: 'running' };
  return { label: 'CI not started', badge: 'suggested' };
}

/** Error text from a failed admin API call, or a fallback. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { body?: { message?: unknown } } | null)?.body?.message;
  return typeof message === 'string' && message.length > 0 ? message : fallback;
}
